import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, Plus, Trash2, Edit2, RefreshCw, Settings,
  Upload, FolderOpen, CheckCircle, AlertCircle, Save,
  GripVertical, Eye, EyeOff, List, Mail, Bell
} from 'lucide-react';

const OnboardingUnificado = () => {
  const [activeTab, setActiveTab] = useState('documentos');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Categorías de documentos
  const [categorias, setCategorias] = useState([]);
  const [isCreateCatOpen, setIsCreateCatOpen] = useState(false);
  const [isEditCatOpen, setIsEditCatOpen] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [categoriaForm, setCategoriaForm] = useState({
    nombre: '',
    descripcion: '',
    obligatorio: true,
    orden: 0,
    tipos_archivo: ['pdf', 'jpg', 'png'],
    tamano_maximo: 10
  });
  
  // Campos de onboarding
  const [campos, setCampos] = useState([]);
  const [isCreateCampoOpen, setIsCreateCampoOpen] = useState(false);
  const [isEditCampoOpen, setIsEditCampoOpen] = useState(false);
  const [selectedCampo, setSelectedCampo] = useState(null);
  const [campoForm, setCampoForm] = useState({
    nombre: '',
    label: '',
    tipo: 'text',
    obligatorio: true,
    orden: 0,
    opciones: '',
    placeholder: '',
    validacion: ''
  });
  
  // Configuración general
  const [config, setConfig] = useState({
    dias_vencimiento: 30,
    notificacion_recordatorio: true,
    dias_recordatorio: 7,
    aprobacion_automatica: false,
    email_bienvenida: true,
    email_rechazo: true,
    email_aprobacion: true
  });

  // Configuración de aprobación
  const [approvalConfig, setApprovalConfig] = useState({
    niveles_aprobacion: 1,
    require_different_approvers: false
  });
  const [savingApproval, setSavingApproval] = useState(false);
  const [flowV2, setFlowV2] = useState(null);  // Flujo V2 de onboarding si existe

  const TIPOS_CAMPO = [
    { value: 'text', label: 'Texto' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Teléfono' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Fecha' },
    { value: 'select', label: 'Selección' },
    { value: 'textarea', label: 'Texto Largo' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'file', label: 'Archivo' },
  ];

  useEffect(() => {
    loadData();
    loadApprovalConfig();
  }, []);

  const loadApprovalConfig = async () => {
    try {
      const [configRes, flowsRes] = await Promise.all([
        api.get('/onboarding/config/aprobacion'),
        api.get('/approval-flows/v2/flows?modulo=ONBOARDING').catch(() => ({ data: { flows: [] } }))
      ]);
      
      setApprovalConfig(configRes.data);
      
      // Buscar flujo activo de onboarding
      const activeFlow = flowsRes.data?.flows?.find(f => f.activo);
      if (activeFlow) {
        setFlowV2(activeFlow);
        // Sincronizar niveles con el flujo V2
        setApprovalConfig(prev => ({
          ...prev,
          niveles_aprobacion: activeFlow.niveles_aprobacion || prev.niveles_aprobacion
        }));
      }
    } catch (error) {
      console.error('Error loading approval config:', error);
    }
  };

  const handleSaveApprovalConfig = async () => {
    setSavingApproval(true);
    try {
      await api.put(`/onboarding/config/aprobacion?niveles_aprobacion=${approvalConfig.niveles_aprobacion}&require_different_approvers=${approvalConfig.require_different_approvers}`);
      toast.success(`Configuración actualizada: ${approvalConfig.niveles_aprobacion} nivel(es) de aprobación`);
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSavingApproval(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, camposRes, configRes] = await Promise.all([
        api.get('/onboarding-config/categorias-documentos').catch(() => ({ data: [] })),
        api.get('/onboarding-config/campos').catch(() => ({ data: [] })),
        api.get('/onboarding-config').catch(() => ({ data: {} }))
      ]);
      setCategorias(Array.isArray(catRes.data) ? catRes.data : catRes.data?.categorias || []);
      setCampos(Array.isArray(camposRes.data) ? camposRes.data : []);
      setConfig(prev => ({ ...prev, ...configRes.data }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Categorías CRUD
  const handleCreateCategoria = async () => {
    if (!categoriaForm.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/onboarding-config/categorias-documentos', categoriaForm);
      toast.success('Categoría creada');
      setIsCreateCatOpen(false);
      setCategoriaForm({ nombre: '', descripcion: '', obligatorio: true, orden: 0, tipos_archivo: ['pdf', 'jpg', 'png'], tamano_maximo: 10 });
      loadData();
    } catch (error) {
      toast.error('Error al crear categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategoria = async () => {
    if (!selectedCategoria) return;
    
    setSaving(true);
    try {
      await api.put(`/onboarding-config/categorias-documentos/${selectedCategoria.id}`, categoriaForm);
      toast.success('Categoría actualizada');
      setIsEditCatOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategoria = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.nombre}"?`)) return;
    
    try {
      await api.delete(`/onboarding-config/categorias-documentos/${cat.id}`);
      toast.success('Categoría eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
  };

  // Campos CRUD
  const handleCreateCampo = async () => {
    if (!campoForm.nombre || !campoForm.label) {
      toast.error('Nombre y label son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...campoForm,
        opciones: campoForm.tipo === 'select' ? campoForm.opciones.split(',').map(o => o.trim()) : []
      };
      await api.post('/onboarding-config/campos', payload);
      toast.success('Campo creado');
      setIsCreateCampoOpen(false);
      setCampoForm({ nombre: '', label: '', tipo: 'text', obligatorio: true, orden: 0, opciones: '', placeholder: '', validacion: '' });
      loadData();
    } catch (error) {
      toast.error('Error al crear campo');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCampo = async () => {
    if (!selectedCampo) return;
    
    setSaving(true);
    try {
      const payload = {
        ...campoForm,
        opciones: campoForm.tipo === 'select' ? campoForm.opciones.split(',').map(o => o.trim()) : []
      };
      await api.put(`/onboarding-config/campos/${selectedCampo.id}`, payload);
      toast.success('Campo actualizado');
      setIsEditCampoOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar campo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampo = async (campo) => {
    if (!window.confirm(`¿Eliminar el campo "${campo.label}"?`)) return;
    
    try {
      await api.delete(`/onboarding-config/campos/${campo.id}`);
      toast.success('Campo eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar campo');
    }
  };

  // Config general
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/onboarding-config', config);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const openEditCategoria = (cat) => {
    setSelectedCategoria(cat);
    setCategoriaForm({
      nombre: cat.nombre,
      descripcion: cat.descripcion || '',
      obligatorio: cat.obligatorio !== false,
      orden: cat.orden || 0,
      tipos_archivo: cat.tipos_archivo || ['pdf', 'jpg', 'png'],
      tamano_maximo: cat.tamano_maximo || 10
    });
    setIsEditCatOpen(true);
  };

  const openEditCampo = (campo) => {
    setSelectedCampo(campo);
    setCampoForm({
      nombre: campo.nombre,
      label: campo.label,
      tipo: campo.tipo || 'text',
      obligatorio: campo.obligatorio !== false,
      orden: campo.orden || 0,
      opciones: Array.isArray(campo.opciones) ? campo.opciones.join(', ') : '',
      placeholder: campo.placeholder || '',
      validacion: campo.validacion || ''
    });
    setIsEditCampoOpen(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Upload size={28} className="text-emerald-600" />
            Configuración de Onboarding
          </h1>
          <p className="text-slate-600 mt-1">Configure los documentos, campos y opciones del proceso de onboarding</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white">
          <div className="text-2xl font-bold text-slate-900">{categorias.length}</div>
          <div className="text-sm text-slate-600">Categorías de Documentos</div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="text-2xl font-bold text-emerald-700">
            {categorias.filter(c => c.obligatorio).length}
          </div>
          <div className="text-sm text-emerald-600">Obligatorios</div>
        </Card>
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-2xl font-bold text-blue-700">{campos.length}</div>
          <div className="text-sm text-blue-600">Campos Configurados</div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="text-2xl font-bold text-amber-700">{config.dias_vencimiento}</div>
          <div className="text-sm text-amber-600">Días para Completar</div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border p-1">
          <TabsTrigger 
            value="documentos" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'documentos' ? '#d1fae5' : 'transparent',
              color: activeTab === 'documentos' ? '#047857' : '#475569'
            }}
          >
            <FolderOpen size={16} className="mr-2" />
            Documentos ({categorias.length})
          </TabsTrigger>
          <TabsTrigger 
            value="campos" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'campos' ? '#dbeafe' : 'transparent',
              color: activeTab === 'campos' ? '#1e40af' : '#475569'
            }}
          >
            <List size={16} className="mr-2" />
            Campos ({campos.length})
          </TabsTrigger>
          <TabsTrigger 
            value="notificaciones" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'notificaciones' ? '#fef3c7' : 'transparent',
              color: activeTab === 'notificaciones' ? '#b45309' : '#475569'
            }}
          >
            <Bell size={16} className="mr-2" />
            Notificaciones
          </TabsTrigger>
          <TabsTrigger 
            value="general" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'general' ? '#f3e8ff' : 'transparent',
              color: activeTab === 'general' ? '#7c3aed' : '#475569'
            }}
          >
            <Settings size={16} className="mr-2" />
            General
          </TabsTrigger>
        </TabsList>

        {/* Tab: Documentos */}
        <TabsContent value="documentos">
          <Card className="p-6 bg-white">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Categorías de Documentos</h2>
              <Button onClick={() => setIsCreateCatOpen(true)} style={{ backgroundColor: '#059669', color: 'white' }}>
                <Plus size={16} className="mr-2" />
                Nueva Categoría
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Orden</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Tipos Permitidos</TableHead>
                  <TableHead>Tamaño Máx.</TableHead>
                  <TableHead>Obligatorio</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categorias.sort((a, b) => (a.orden || 0) - (b.orden || 0)).map((cat, idx) => (
                  <TableRow key={cat.id || idx}>
                    <TableCell>
                      <Badge variant="outline">{cat.orden || idx + 1}</Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{cat.nombre}</p>
                        {cat.descripcion && (
                          <p className="text-sm text-slate-500">{cat.descripcion}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(cat.tipos_archivo || ['pdf']).map(t => (
                          <Badge key={t} variant="outline" className="text-xs">.{t}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{cat.tamano_maximo || 10} MB</TableCell>
                    <TableCell>
                      {cat.obligatorio !== false ? (
                        <Badge className="bg-red-100 text-red-700">Obligatorio</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">Opcional</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditCategoria(cat)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteCategoria(cat)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab: Campos */}
        <TabsContent value="campos">
          <Card className="p-6 bg-white">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Campos del Formulario</h2>
              <Button onClick={() => setIsCreateCampoOpen(true)} style={{ backgroundColor: '#2563eb', color: 'white' }}>
                <Plus size={16} className="mr-2" />
                Nuevo Campo
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Orden</TableHead>
                  <TableHead>Campo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Obligatorio</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campos.sort((a, b) => (a.orden || 0) - (b.orden || 0)).map((campo, idx) => (
                  <TableRow key={campo.id || idx}>
                    <TableCell>
                      <Badge variant="outline">{campo.orden || idx + 1}</Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{campo.label}</p>
                        <p className="text-sm text-slate-500 font-mono">{campo.nombre}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-700">
                        {TIPOS_CAMPO.find(t => t.value === campo.tipo)?.label || campo.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {campo.obligatorio !== false ? (
                        <CheckCircle size={18} className="text-green-600" />
                      ) : (
                        <AlertCircle size={18} className="text-slate-400" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditCampo(campo)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteCampo(campo)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab: Notificaciones */}
        <TabsContent value="notificaciones">
          <Card className="p-6 bg-white">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Configuración de Notificaciones</h2>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Email de Bienvenida</p>
                  <p className="text-sm text-slate-500">Enviar email cuando se inicia el proceso de onboarding</p>
                </div>
                <Switch
                  checked={config.email_bienvenida}
                  onCheckedChange={(v) => setConfig({ ...config, email_bienvenida: v })}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Email de Aprobación</p>
                  <p className="text-sm text-slate-500">Notificar cuando el onboarding es aprobado</p>
                </div>
                <Switch
                  checked={config.email_aprobacion}
                  onCheckedChange={(v) => setConfig({ ...config, email_aprobacion: v })}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Email de Rechazo</p>
                  <p className="text-sm text-slate-500">Notificar cuando se rechaza algún documento</p>
                </div>
                <Switch
                  checked={config.email_rechazo}
                  onCheckedChange={(v) => setConfig({ ...config, email_rechazo: v })}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Recordatorios Automáticos</p>
                  <p className="text-sm text-slate-500">Enviar recordatorios antes del vencimiento</p>
                </div>
                <Switch
                  checked={config.notificacion_recordatorio}
                  onCheckedChange={(v) => setConfig({ ...config, notificacion_recordatorio: v })}
                />
              </div>
              
              {config.notificacion_recordatorio && (
                <div className="ml-4 p-4 border-l-4 border-amber-400 bg-amber-50 rounded-r-lg">
                  <Label>Días antes del vencimiento para enviar recordatorio</Label>
                  <Input
                    type="number"
                    value={config.dias_recordatorio}
                    onChange={(e) => setConfig({ ...config, dias_recordatorio: parseInt(e.target.value) || 7 })}
                    className="w-32 mt-2"
                    min={1}
                    max={30}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveConfig} disabled={saving} style={{ backgroundColor: '#b45309', color: 'white' }}>
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Tab: General */}
        <TabsContent value="general">
          <Card className="p-6 bg-white">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Configuración General</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Días para completar el onboarding</Label>
                <Input
                  type="number"
                  value={config.dias_vencimiento}
                  onChange={(e) => setConfig({ ...config, dias_vencimiento: parseInt(e.target.value) || 30 })}
                  className="mt-2"
                  min={1}
                  max={365}
                />
                <p className="text-sm text-slate-500 mt-1">Después de este tiempo, el proceso expira</p>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Aprobación Automática</p>
                  <p className="text-sm text-slate-500">Aprobar automáticamente cuando todos los documentos están completos</p>
                </div>
                <Switch
                  checked={config.aprobacion_automatica}
                  onCheckedChange={(v) => setConfig({ ...config, aprobacion_automatica: v })}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveConfig} disabled={saving} style={{ backgroundColor: '#7c3aed', color: 'white' }}>
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </div>
          </Card>

          {/* Card de Flujo de Aprobación */}
          <Card className="p-6 bg-white mt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Settings size={20} className="text-purple-600" />
              Flujo de Aprobación
            </h2>
            
            {flowV2 ? (
              // Si existe un flujo V2 configurado, mostrar su info
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-purple-900">{flowV2.nombre}</span>
                      {flowV2.activo && <Badge className="bg-green-100 text-green-700">Activo</Badge>}
                      {flowV2.protegido && <Badge className="bg-amber-100 text-amber-700">🔒 Protegido</Badge>}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-purple-600 font-medium">Niveles de Aprobación</p>
                      <p className="text-purple-900">{flowV2.niveles_aprobacion} {flowV2.niveles_aprobacion === 1 ? 'nivel' : 'niveles'}</p>
                    </div>
                    <div>
                      <p className="text-purple-600 font-medium">SLA</p>
                      <p className="text-purple-900">{flowV2.sla_horas || '-'} horas</p>
                    </div>
                    <div>
                      <p className="text-purple-600 font-medium">Aprobadores Nivel 1</p>
                      <p className="text-purple-900">{flowV2.aprobadores?.map(a => a.valor).join(', ') || 'No definidos'}</p>
                    </div>
                    {flowV2.niveles_aprobacion >= 2 && (
                      <div>
                        <p className="text-purple-600 font-medium">Aprobadores Nivel 2</p>
                        <p className="text-purple-900">{flowV2.aprobadores_nivel_2?.map(a => a.valor).join(', ') || 'No definidos'}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={() => window.location.href = '/m/configuracion/flujos-aprobacion'}
                    style={{ backgroundColor: '#7c3aed', color: 'white' }}
                  >
                    <Settings size={16} className="mr-2" />
                    Configurar Flujo Completo
                  </Button>
                </div>
              </div>
            ) : (
              // Si no existe flujo V2, mostrar selector simple
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium mb-3 block">Niveles de Aprobación</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      onClick={() => setApprovalConfig({ ...approvalConfig, niveles_aprobacion: 1 })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        approvalConfig.niveles_aprobacion === 1
                          ? 'border-green-500 bg-green-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          approvalConfig.niveles_aprobacion === 1 ? 'border-green-500 bg-green-500' : 'border-slate-300'
                        }`}>
                          {approvalConfig.niveles_aprobacion === 1 && <CheckCircle size={14} className="text-white" />}
                        </div>
                        <span className="font-medium">1 Nivel (Simple)</span>
                      </div>
                      <p className="text-sm text-slate-500 ml-9">
                        Un solo revisor aprueba y la empresa se crea inmediatamente.
                      </p>
                    </div>
                    
                    <div
                      onClick={() => setApprovalConfig({ ...approvalConfig, niveles_aprobacion: 2 })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        approvalConfig.niveles_aprobacion === 2
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          approvalConfig.niveles_aprobacion === 2 ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
                        }`}>
                          {approvalConfig.niveles_aprobacion === 2 && <CheckCircle size={14} className="text-white" />}
                        </div>
                        <span className="font-medium">2 Niveles (Doble Control)</span>
                      </div>
                      <p className="text-sm text-slate-500 ml-9">
                        Requiere aprobación de dos revisores diferentes.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => window.location.href = '/m/configuracion/flujos-aprobacion'}
                  >
                    <Settings size={16} className="mr-2" />
                    Configuración Avanzada
                  </Button>
                  <Button 
                    onClick={handleSaveApprovalConfig} 
                    disabled={savingApproval}
                    style={{ backgroundColor: '#7c3aed', color: 'white' }}
                  >
                    <Save size={16} className="mr-2" />
                    {savingApproval ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Create Categoria */}
      <Dialog open={isCreateCatOpen} onOpenChange={setIsCreateCatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Categoría de Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={categoriaForm.nombre}
                onChange={(e) => setCategoriaForm({ ...categoriaForm, nombre: e.target.value })}
                placeholder="Ej: DNI, Contrato Social"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={categoriaForm.descripcion}
                onChange={(e) => setCategoriaForm({ ...categoriaForm, descripcion: e.target.value })}
                placeholder="Descripción del documento requerido"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={categoriaForm.orden}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, orden: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Tamaño Máximo (MB)</Label>
                <Input
                  type="number"
                  value={categoriaForm.tamano_maximo}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, tamano_maximo: parseInt(e.target.value) || 10 })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={categoriaForm.obligatorio}
                onCheckedChange={(v) => setCategoriaForm({ ...categoriaForm, obligatorio: v })}
              />
              <Label>Documento obligatorio</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateCatOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCategoria} disabled={saving} style={{ backgroundColor: '#059669', color: 'white' }}>
              {saving ? 'Creando...' : 'Crear Categoría'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Categoria */}
      <Dialog open={isEditCatOpen} onOpenChange={setIsEditCatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={categoriaForm.nombre}
                onChange={(e) => setCategoriaForm({ ...categoriaForm, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={categoriaForm.descripcion}
                onChange={(e) => setCategoriaForm({ ...categoriaForm, descripcion: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={categoriaForm.orden}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, orden: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Tamaño Máximo (MB)</Label>
                <Input
                  type="number"
                  value={categoriaForm.tamano_maximo}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, tamano_maximo: parseInt(e.target.value) || 10 })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={categoriaForm.obligatorio}
                onCheckedChange={(v) => setCategoriaForm({ ...categoriaForm, obligatorio: v })}
              />
              <Label>Documento obligatorio</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditCatOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateCategoria} disabled={saving} style={{ backgroundColor: '#059669', color: 'white' }}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Create Campo */}
      <Dialog open={isCreateCampoOpen} onOpenChange={setIsCreateCampoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Campo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre interno *</Label>
              <Input
                value={campoForm.nombre}
                onChange={(e) => setCampoForm({ ...campoForm, nombre: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                placeholder="nombre_campo"
              />
            </div>
            <div>
              <Label>Etiqueta (Label) *</Label>
              <Input
                value={campoForm.label}
                onChange={(e) => setCampoForm({ ...campoForm, label: e.target.value })}
                placeholder="Nombre visible para el usuario"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={campoForm.tipo} onValueChange={(v) => setCampoForm({ ...campoForm, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CAMPO.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={campoForm.orden}
                  onChange={(e) => setCampoForm({ ...campoForm, orden: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            {campoForm.tipo === 'select' && (
              <div>
                <Label>Opciones (separadas por coma)</Label>
                <Input
                  value={campoForm.opciones}
                  onChange={(e) => setCampoForm({ ...campoForm, opciones: e.target.value })}
                  placeholder="Opción 1, Opción 2, Opción 3"
                />
              </div>
            )}
            <div>
              <Label>Placeholder</Label>
              <Input
                value={campoForm.placeholder}
                onChange={(e) => setCampoForm({ ...campoForm, placeholder: e.target.value })}
                placeholder="Texto de ayuda en el campo"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={campoForm.obligatorio}
                onCheckedChange={(v) => setCampoForm({ ...campoForm, obligatorio: v })}
              />
              <Label>Campo obligatorio</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateCampoOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCampo} disabled={saving} style={{ backgroundColor: '#2563eb', color: 'white' }}>
              {saving ? 'Creando...' : 'Crear Campo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Campo */}
      <Dialog open={isEditCampoOpen} onOpenChange={setIsEditCampoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Campo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre interno</Label>
              <Input value={campoForm.nombre} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label>Etiqueta (Label)</Label>
              <Input
                value={campoForm.label}
                onChange={(e) => setCampoForm({ ...campoForm, label: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={campoForm.tipo} onValueChange={(v) => setCampoForm({ ...campoForm, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CAMPO.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={campoForm.orden}
                  onChange={(e) => setCampoForm({ ...campoForm, orden: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            {campoForm.tipo === 'select' && (
              <div>
                <Label>Opciones (separadas por coma)</Label>
                <Input
                  value={campoForm.opciones}
                  onChange={(e) => setCampoForm({ ...campoForm, opciones: e.target.value })}
                />
              </div>
            )}
            <div>
              <Label>Placeholder</Label>
              <Input
                value={campoForm.placeholder}
                onChange={(e) => setCampoForm({ ...campoForm, placeholder: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={campoForm.obligatorio}
                onCheckedChange={(v) => setCampoForm({ ...campoForm, obligatorio: v })}
              />
              <Label>Campo obligatorio</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditCampoOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateCampo} disabled={saving} style={{ backgroundColor: '#2563eb', color: 'white' }}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingUnificado;
